import { memo, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { Skeleton, Text } from '@fhs/ui';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { CHECKOUT_ORDER_SUMMARY_SUBTOTAL } from '@fhs-legacy/frontend/src/utils/test-ids';

import { OriginalPriceDisplay } from '../../cart-components/item/item-profile/item-price/item-price-components';

function SubtotalRow() {
  const { serverOrder, serverOrderLoading, cartEntries } = useCartContext();
  const enableZeroPricedOrder = useFlag('enable-zero-cart-total-order');

  const donationAmount = useMemo(
    () => cartEntries.find(entry => entry.isDonation)?.price || 0,
    [cartEntries]
  );

  const taxCents = serverOrder?.cart?.taxCents || 0;

  const { subtotalCents, discountAmount } = useMemo(() => {
    const subTotal = serverOrder?.cart?.subTotalCents || 0;
    const discounts = serverOrder?.cart?.discounts || [];
    const totalDiscount = discounts.reduce((sum, d) => sum + d.value, 0);

    return {
      subtotalCents: subTotal,
      discountAmount: totalDiscount,
    };
  }, [serverOrder?.cart?.subTotalCents, serverOrder?.cart?.discounts]);

  if (serverOrderLoading || !serverOrder?.cart || (!enableZeroPricedOrder && taxCents === 0)) {
    return (
      <View testID={CHECKOUT_ORDER_SUMMARY_SUBTOTAL} style={styles.container}>
        <Text.Paragraph size="sm">Subtotal</Text.Paragraph>
        <Skeleton style={styles.skeleton} />
      </View>
    );
  }
  const formattedSubtotal = ((subtotalCents + discountAmount) / 100).toFixed(2);

  return (
    <View
      testID={CHECKOUT_ORDER_SUMMARY_SUBTOTAL}
      style={styles.container}
      accessibilityRole="text"
      accessibilityLabel={`Subtotal: ${formattedSubtotal}`}
    >
      <Text.Paragraph size="sm" style={styles.label}>
        Subtotal
      </Text.Paragraph>
      <View style={styles.priceContainer}>
        <OriginalPriceDisplay subtotalID size="sm" price={subtotalCents - donationAmount + discountAmount} />
      </View>
    </View>
  );
}

export default memo(SubtotalRow);

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 4,
    marginHorizontal: 28,
  },

  label: {
    alignSelf: 'center',
  },

  priceContainer: {
    flexDirection: 'row',
    gap: 9,
    alignItems: 'center',
  },

  skeleton: {
    height: 16,
    width: 48,
    borderRadius: 4,
  },
});
